import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Edit, Trash2, DollarSign, Coins } from 'lucide-react'
import { currencyDenominationsApi } from '../../lib/api'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface Denomination {
  id: string
  denomination: number
  name: string
  type: 'bill' | 'coin'
  is_active: boolean
  sort_order: number
}

const AdminCurrencyDenominations = () => {
  const navigate = useNavigate()
  const [denominations, setDenominations] = useState<Denomination[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<Denomination | null>(null)
  const [formData, setFormData] = useState({
    denomination: '',
    name: '',
    type: 'bill' as 'bill' | 'coin',
    sort_order: 0
  })

  useEffect(() => {
    loadDenominations()
  }, [])

  const loadDenominations = async () => {
    const cacheKey = 'admin_currency_denominations'
    
    // Intentar cargar desde cache primero
    const cachedDenominations = getCacheStale<Denomination[]>(cacheKey)
    if (cachedDenominations && cachedDenominations.length > 0) {
      setDenominations(cachedDenominations)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await currencyDenominationsApi.getDenominations()
      if (response.success) {
        const sortedData = response.data.sort((a: Denomination, b: Denomination) => b.denomination - a.denomination)
        setDenominations(sortedData)
        
        // Guardar en cache (1 hora de TTL - denominaciones cambian poco)
        setCache(cacheKey, sortedData, 60 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading denominations:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedDenominations) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar denominaciones')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editing) {
        await currencyDenominationsApi.updateDenomination(editing.id, {
          name: formData.name,
          type: formData.type,
          sort_order: formData.sort_order
        })
        toast.success('Denominación actualizada')
      } else {
        await currencyDenominationsApi.createDenomination({
          denomination: parseFloat(formData.denomination),
          name: formData.name,
          type: formData.type,
          sort_order: formData.sort_order
        })
        toast.success('Denominación creada')
      }
      
      setShowModal(false)
      setEditing(null)
      setFormData({ denomination: '', name: '', type: 'bill', sort_order: 0 })
      loadDenominations()
    } catch (error: any) {
      console.error('Error saving denomination:', error)
      toast.error(error.response?.data?.message || 'Error al guardar denominación')
    }
  }

  const handleEdit = (denom: Denomination) => {
    setEditing(denom)
    setFormData({
      denomination: denom.denomination.toString(),
      name: denom.name,
      type: denom.type,
      sort_order: denom.sort_order
    })
    setShowModal(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta denominación?')) return

    try {
      await currencyDenominationsApi.deleteDenomination(id)
      toast.success('Denominación eliminada')
      loadDenominations()
    } catch (error: any) {
      console.error('Error deleting denomination:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar denominación')
    }
  }

  const handleToggleActive = async (denom: Denomination) => {
    try {
      await currencyDenominationsApi.updateDenomination(denom.id, {
        is_active: !denom.is_active
      })
      toast.success(`Denominación ${denom.is_active ? 'desactivada' : 'activada'}`)
      loadDenominations()
    } catch (error: any) {
      console.error('Error toggling denomination:', error)
      toast.error('Error al actualizar denominación')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pb-24">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-2xl border-b border-white/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 lg:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-3 lg:p-4 -ml-2 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all active:scale-95 shadow-lg shadow-black/5"
            >
              <ArrowLeft className="w-6 h-6 lg:w-7 lg:h-7 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Denominaciones de Billetes/Monedas
              </h1>
              <p className="text-xs lg:text-sm text-gray-500 font-medium mt-0.5">
                Configura las denominaciones disponibles en el sistema
              </p>
            </div>
            <button
              onClick={() => {
                setEditing(null)
                setFormData({ denomination: '', name: '', type: 'bill', sort_order: 0 })
                setShowModal(true)
              }}
              className="px-4 lg:px-6 py-3 lg:py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl font-bold text-sm lg:text-base shadow-lg shadow-blue-500/30 active:scale-95 transition-transform flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden lg:inline">Nueva</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
        <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {denominations.map((denom) => (
              <div
                key={denom.id}
                className={`rounded-2xl p-5 border-2 ${
                  denom.is_active
                    ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {denom.type === 'bill' ? (
                      <DollarSign className="w-8 h-8 text-emerald-600" />
                    ) : (
                      <Coins className="w-8 h-8 text-amber-600" />
                    )}
                    <div>
                      <p className="font-bold text-lg text-gray-900">{denom.name}</p>
                      <p className="text-sm text-gray-600">RD${denom.denomination.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Tipo: {denom.type === 'bill' ? 'Billete' : 'Moneda'} | 
                        Orden: {denom.sort_order}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(denom)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold ${
                        denom.is_active
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {denom.is_active ? 'Activa' : 'Inactiva'}
                    </button>
                    <button
                      onClick={() => handleEdit(denom)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-xl active:scale-95"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(denom.id)}
                      className="p-2 bg-red-100 text-red-600 rounded-xl active:scale-95"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 lg:p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-6">
              {editing ? 'Editar Denominación' : 'Nueva Denominación'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editing && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Valor (RD$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={formData.denomination}
                    onChange={(e) => setFormData({ ...formData, denomination: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-blue-500 focus:outline-none"
                  placeholder="Ej: Billete de 100"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'bill' | 'coin' })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-blue-500 focus:outline-none"
                >
                  <option value="bill">Billete</option>
                  <option value="coin">Moneda</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Orden de Visualización
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Mayor número = aparece primero</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    setEditing(null)
                    setFormData({ denomination: '', name: '', type: 'bill', sort_order: 0 })
                  }}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-2xl font-bold active:scale-95 transition-transform"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl font-bold active:scale-95 transition-transform"
                >
                  {editing ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCurrencyDenominations

